import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Plus } from "lucide-react";
import request from "@/shared/api/request";
import useRights from "@/shared/api/useRights";
import DeviceMenu, { type DeviceTabType } from "./DeviceMenu";
import { parseRunningHours } from "./runningHourInput";

dayjs.extend(utc);

interface Measurement {
  id: number;
  runningHours: number;
  createdAt: string;
  createdBy: { id: number; firstName: string; name: string } | null;
}

type Editor = { mode: "create" } | { mode: "correct"; measurement: Measurement };
type SaveMeasurement = { mode: "create"; runningHours: number } | { mode: "correct"; id: number; runningHours: number };

export default function DeviceRunningHours({ deviceId, deviceNumber, image, setVisiblePage }: {
  deviceId: number;
  deviceNumber: string;
  image: string | null | undefined;
  setVisiblePage: (page: DeviceTabType) => void;
}) {
  const rights = useRights();
  const canEdit = "DISPOSITION" in rights;
  const client = useQueryClient();
  const [editor, setEditor] = useState<Editor | null>(null);
  const selected = editor?.mode === "correct" ? editor.measurement : null;
  const isCreating = editor?.mode === "create";
  const [value, setValue] = useState("");
  const hours = parseRunningHours(value);
  const query = useQuery<Measurement[], AxiosResponse<{ error?: string }>>({
    queryKey: ["getRunningHourMeasurements", deviceId],
    queryFn: () => request({ url: `/data/resources/${deviceId}/running-hours`, method: "GET" }),
    enabled: deviceId > 0,
  });
  const lastReading = query.data?.[0];
  const belowLastReading = isCreating && hours !== null && lastReading !== undefined && hours < lastReading.runningHours;
  const canSave = hours !== null && !belowLastReading && (isCreating ? query.isSuccess : canEdit);
  const mutation = useMutation<unknown, AxiosResponse<{ error?: string }>, SaveMeasurement>({
    mutationFn: (data) => data.mode === "create"
      ? request({ url: "/entity/meterReading/", method: "POST", data: { resource: deviceId, runningHours: data.runningHours } })
      : request({ url: `/data/resources/${deviceId}/running-hours/${data.id}`, method: "PATCH", data: { runningHours: data.runningHours } }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["getRunningHourMeasurements", deviceId] }),
        client.invalidateQueries({ queryKey: ["getDeviceById"], predicate: (q) => String(q.queryKey[1]) === String(deviceId) }),
        client.invalidateQueries({ queryKey: ["getDeviceByNumber", deviceNumber] }),
        client.invalidateQueries({ queryKey: ["getDeviceEvents", deviceId] }),
        client.invalidateQueries({ queryKey: ["getFuelingEvents", deviceId] }),
        client.invalidateQueries({ queryKey: ["getServiceByUser"] }),
        client.invalidateQueries({ queryKey: ["getEvents"] }),
      ]);
      setEditor(null);
    },
  });
  const columns = useMemo<GridColDef<Measurement>[]>(() => [
    { field: "createdAt", headerName: "Erfasst am", type: "dateTime", flex: 1, minWidth: 160,
      valueGetter: (_value, row) => dayjs.utc(row.createdAt).toDate(),
      valueFormatter: (date: Date | null) => date ? dayjs(date).format("DD.MM.YYYY HH:mm") : "" },
    { field: "runningHours", headerName: "Betriebsstunden", type: "number", flex: 0.8, minWidth: 140,
      valueFormatter: (amount: number) => `${amount.toLocaleString("de-DE")} h` },
    { field: "createdBy", headerName: "Erfasser", flex: 1, minWidth: 150,
      valueGetter: (_value, row) => row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.name}`.trim() : "Unbekannt" },
  ], []);
  const edit = (row: Measurement) => {
    if (!canEdit) return;
    mutation.reset();
    setValue(String(row.runningHours));
    setEditor({ mode: "correct", measurement: row });
  };
  const create = () => {
    mutation.reset();
    setValue("");
    setEditor({ mode: "create" });
  };
  const close = () => { if (!mutation.isPending) setEditor(null); };
  return <>
    <DeviceMenu type="HOURS" image={image} setType={setVisiblePage} plusButton={
      <Button onClick={create} disabled={!query.isSuccess || mutation.isPending} variant="contained" size="small"
        aria-label="Betriebsstunden erfassen" title="Betriebsstunden erfassen"
        sx={{
          minWidth: 0,
          bgcolor: "common.white",
          color: "common.black",
          border: "1px solid",
          borderColor: "grey.300",
          boxShadow: "none",
          "&:hover": { bgcolor: "grey.100", boxShadow: "none", borderColor: "grey.400" },
        }}>
        <Plus />
      </Button>
    } />
    <Box sx={{ border: "1px solid #002169", borderTop: "none", p: 1 }}>
      {query.isPending ? <CircularProgress size={24} /> : query.isError ?
        <Alert severity="error" action={<Button onClick={() => void query.refetch()}>Erneut laden</Button>}>Betriebsstunden konnten nicht geladen werden.</Alert> :
        query.data.length === 0 ? <Typography sx={{ p: 2 }}>Keine Betriebsstundenmessungen erfasst.</Typography> :
        <DataGridPro<Measurement> rows={query.data} columns={canEdit ? [...columns, {
          field: "edit", headerName: "", sortable: false, filterable: false, width: 120, disableExport: true,
          renderCell: ({ row }) => <Button size="small" onClick={() => edit(row)}>Korrigieren</Button>,
        }] : columns} autoHeight rowHeight={40} loading={query.isFetching}
          initialState={{ sorting: { sortModel: [{ field: "createdAt", sort: "desc" }] } }}
          onRowDoubleClick={({ row }) => edit(row)} disableRowSelectionOnClick />}
    </Box>
    <Dialog open={editor !== null} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{isCreating ? "Betriebsstunden erfassen" : "Betriebsstunden korrigieren"}</DialogTitle>
      <DialogContent>
        {isCreating ? <Typography sx={{ mb: 2 }}>{lastReading ? `Letzter Zählerstand: ${lastReading.runningHours.toLocaleString("de-DE")} h.` : "Noch kein Zählerstand bekannt."}</Typography> :
          <Typography sx={{ mb: 2 }}>Messung vom {selected ? dayjs.utc(selected.createdAt).local().format("DD.MM.YYYY HH:mm") : ""}. Datum und ursprünglicher Erfasser bleiben erhalten.</Typography>}
        <TextField autoFocus fullWidth label="Betriebsstunden" value={value} disabled={mutation.isPending}
          onChange={(event) => setValue(event.target.value)} inputProps={{ inputMode: "numeric" }}
          error={hours === null || belowLastReading} helperText={hours === null ? "Bitte gültige ganze, nicht negative Betriebsstunden eingeben." : belowLastReading ? "Der Wert darf nicht niedriger als der letzte Zählerstand sein." : " "} />
        {mutation.isError && <Alert severity="error">{mutation.error?.data?.error ?? (isCreating ? "Die Messung konnte nicht gespeichert werden." : "Die Korrektur konnte nicht gespeichert werden.")}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={mutation.isPending}>Abbrechen</Button>
        <Button variant="contained" disabled={!canSave || mutation.isPending} onClick={() => {
          if (editor && canSave && hours !== null) mutation.mutate(editor.mode === "create"
            ? { mode: "create", runningHours: hours }
            : { mode: "correct", id: editor.measurement.id, runningHours: hours });
        }}>{mutation.isPending ? "Speichert …" : "Speichern"}</Button>
      </DialogActions>
    </Dialog>
  </>;
}
